import { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Zap, Plus, Trash2, MapPin, AlertCircle, CheckCircle, Calculator,
  ChevronDown, ChevronUp, Info, Loader2,
} from 'lucide-react';
import { useAppAuth } from '../hooks/useAppAuth';
import { createApiClient, autoQuoteService } from '../services/api';
import { VehicleType, AutoQuoteMode } from '../types';
import type {
  AutoQuoteRuleDto, SaveAutoQuoteRuleRequest, AutoQuoteBand,
  AutoQuoteArea, AutoQuoteUplift, AutoQuotePreviewDto,
} from '../types';

// ─────────────────────────────────────────────
//  Constants
// ─────────────────────────────────────────────

const RADII = [5, 10, 15, 25, 50, 75, 100];

const VEHICLE_OPTIONS: { label: string; value: VehicleType }[] = [
  { label: 'Motorcycle', value: VehicleType.Motorcycle },
  { label: 'Car',        value: VehicleType.Car },
  { label: 'Small Van',  value: VehicleType.SmallVan },
  { label: 'Midi Van',   value: VehicleType.MidiVan },
  { label: 'LWB Van',    value: VehicleType.LWBVan },
  { label: 'Luton Van',  value: VehicleType.LutonVan },
  { label: '7.5T',       value: VehicleType.Tonne7_5 },
  { label: '12T',        value: VehicleType.Tonne12 },
  { label: '18T',        value: VehicleType.Tonne18 },
  { label: '26T',        value: VehicleType.Tonne26 },
  { label: 'Artic',      value: VehicleType.Artic },
];

/**
 * A sensible opening card. New providers get something they can immediately test in the
 * simulator rather than an empty form — the fastest way to understand a rate card is to see
 * one price a job and then change a number.
 */
const DEFAULT_RULE: SaveAutoQuoteRuleRequest = {
  isEnabled: false,
  mode: AutoQuoteMode.ConfirmFirst,
  autoReleaseAfterMinutes: null,
  appliesToSameDay: true,
  appliesToScheduled: true,
  maxJobDistanceMiles: 0,
  calloutFee: 15,
  perExtraStopFee: 8,
  perTonneRate: 0,
  perLdmRate: 0,
  tailLiftSurcharge: 20,
  twoManSurcharge: 30,
  hiabSurcharge: 45,
  sameDaySurcharge: 25,
  outOfHoursSurcharge: 18,
  weekendSurcharge: 12,
  bankHolidaySurcharge: 40,
  workingHoursFrom: 8,
  workingHoursTo: 18,
  fuelLevyPercent: 6.5,
  roundToNearestPence: 500,
  zoneChargeRecoveryFactor: 1,
  hasDartChargeAccount: false,
  hasTflAutoPay: false,
  maxOpenAutoQuotes: 10,
  dailyAutoQuoteCap: 20,
  bands: [
    { bandOrder: 1, fromMiles: 0,  toMiles: 10,   ratePerMile: 2.5 },
    { bandOrder: 2, fromMiles: 10, toMiles: 50,   ratePerMile: 1.6 },
    { bandOrder: 3, fromMiles: 50, toMiles: null, ratePerMile: 1.2 },
  ],
  uplifts: [],
  areas: [],
};

/** Handles full postcodes (LS1 4AP) and outcode districts (LS1) via postcodes.io. */
async function geocodePostcode(postcode: string): Promise<{ lat: number; lng: number }> {
  const cleaned = postcode.trim().toUpperCase();
  const res  = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(cleaned)}`);
  const data = await res.json();
  if (data.result?.latitude) return { lat: data.result.latitude, lng: data.result.longitude };
  const outcode = cleaned.replace(/\s.*$/, '');
  const res2  = await fetch(`https://api.postcodes.io/outcodes/${encodeURIComponent(outcode)}`);
  const data2 = await res2.json();
  if (data2.result?.latitude) return { lat: data2.result.latitude, lng: data2.result.longitude };
  throw new Error('Postcode not found. Try a UK postcode or district, e.g. LS1 or LS1 4AP.');
}

const money = (n: number) => `£${n.toFixed(2)}`;

// ─────────────────────────────────────────────
//  Small presentational pieces
// ─────────────────────────────────────────────

function Section({
  title, subtitle, children, defaultOpen = true,
}: {
  title: string; subtitle?: string; children: React.ReactNode; defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <div>
          <h2 className="font-semibold text-gray-900">{title}</h2>
          {subtitle && <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>}
        </div>
        {open ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {open && <div className="px-5 pb-5 pt-1 border-t border-gray-100">{children}</div>}
    </div>
  );
}

function MoneyField({
  label, value, onChange, hint, suffix = '£',
}: {
  label: string; value: number; onChange: (v: number) => void; hint?: string; suffix?: string;
}) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-gray-600">{label}</span>
      <div className="mt-1 relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{suffix}</span>
        <input
          type="number"
          step="0.01"
          min="0"
          value={Number.isFinite(value) ? value : 0}
          onChange={e => onChange(parseFloat(e.target.value) || 0)}
          className="w-full pl-7 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
      </div>
      {hint && <span className="text-[11px] text-gray-400 mt-1 block">{hint}</span>}
    </label>
  );
}

// ─────────────────────────────────────────────
//  Page
// ─────────────────────────────────────────────

export default function AutoQuoteSettingsPage() {
  const { getToken } = useAppAuth();
  const client  = useMemo(() => createApiClient(getToken), [getToken]);
  const svc     = useMemo(() => autoQuoteService(client), [client]);
  const qc      = useQueryClient();

  const [draft, setDraft]     = useState<SaveAutoQuoteRuleRequest>(DEFAULT_RULE);
  const [error, setError]     = useState<string | null>(null);
  const [saved, setSaved]     = useState(false);

  const { data: rule, isLoading } = useQuery<AutoQuoteRuleDto | null>({
    queryKey: ['auto-quote', 'rule'],
    queryFn : svc.getRule,
  });

  // Seed the form from the server once. The default card is only a starting point for a
  // provider who has never saved one.
  useEffect(() => {
    if (rule) setDraft({ ...rule });
  }, [rule]);

  const saveMutation = useMutation({
    mutationFn: (payload: SaveAutoQuoteRuleRequest) => svc.saveRule(payload),
    onSuccess : () => {
      setError(null);
      setSaved(true);
      window.setTimeout(() => setSaved(false), 3000);
      qc.invalidateQueries({ queryKey: ['auto-quote', 'rule'] });
    },
    onError: (err: { response?: { data?: { message?: string; detail?: string } } }) =>
      setError(err?.response?.data?.message ?? err?.response?.data?.detail ?? 'Could not save your rate card.'),
  });

  const patch = useCallback(
    <K extends keyof SaveAutoQuoteRuleRequest>(key: K, value: SaveAutoQuoteRuleRequest[K]) =>
      setDraft(d => ({ ...d, [key]: value })),
    [],
  );

  // ── Bands ─────────────────────────────────────────────────────────────────
  // Bands must tile the line from zero with no gap. Rather than let a provider create an
  // invalid table and then reject the save, editing a boundary moves its neighbour too, so
  // the table is continuous by construction.
  const setBandBoundary = (index: number, to: number | null) => {
    setDraft(d => {
      const bands = d.bands.map(b => ({ ...b }));
      bands[index].toMiles = to;
      if (index + 1 < bands.length && to !== null) bands[index + 1].fromMiles = to;
      return { ...d, bands };
    });
  };

  const setBandRate = (index: number, rate: number) =>
    setDraft(d => {
      const bands = d.bands.map(b => ({ ...b }));
      bands[index].ratePerMile = rate;
      return { ...d, bands };
    });

  const addBand = () =>
    setDraft(d => {
      const bands = d.bands.map(b => ({ ...b }));
      const last  = bands[bands.length - 1];
      const split = (last?.fromMiles ?? 0) + 50;
      if (last) last.toMiles = split;
      bands.push({
        bandOrder: bands.length + 1,
        fromMiles: split,
        toMiles: null,
        ratePerMile: last?.ratePerMile ?? 1,
      });
      return { ...d, bands };
    });

  const removeBand = (index: number) =>
    setDraft(d => {
      if (d.bands.length <= 1) return d;
      const bands = d.bands.filter((_, i) => i !== index).map((b, i) => ({ ...b, bandOrder: i + 1 }));
      // Re-tile: the first band always starts at zero and the last is always open-ended.
      bands[0].fromMiles = 0;
      for (let i = 1; i < bands.length; i++) bands[i].fromMiles = bands[i - 1].toMiles ?? bands[i].fromMiles;
      bands[bands.length - 1].toMiles = null;
      return { ...d, bands };
    });

  // ── Areas ─────────────────────────────────────────────────────────────────
  const [areaPostcode, setAreaPostcode] = useState('');
  const [areaRadius, setAreaRadius]     = useState(25);
  const [areaBusy, setAreaBusy]         = useState(false);

  const addArea = async () => {
    if (!areaPostcode.trim()) return;
    setAreaBusy(true);
    setError(null);
    try {
      const { lat, lng } = await geocodePostcode(areaPostcode);
      const postcode = areaPostcode.trim().toUpperCase();
      setDraft(d =>
        d.areas.some(a => a.postcode === postcode && a.radiusMiles === areaRadius)
          ? d
          : { ...d, areas: [...d.areas, { postcode, centroidLat: lat, centroidLng: lng, radiusMiles: areaRadius }] },
      );
      setAreaPostcode('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not look that postcode up.');
    } finally {
      setAreaBusy(false);
    }
  };

  const removeArea = (a: AutoQuoteArea) =>
    setDraft(d => ({ ...d, areas: d.areas.filter(x => !(x.postcode === a.postcode && x.radiusMiles === a.radiusMiles)) }));

  // ── Uplifts ───────────────────────────────────────────────────────────────
  const setUplift = (vehicleType: VehicleType, uplift: number) =>
    setDraft(d => {
      const others = d.uplifts.filter(u => u.vehicleType !== vehicleType);
      return { ...d, uplifts: uplift === 0 ? others : [...others, { vehicleType, uplift }] };
    });

  const upliftFor = (vt: VehicleType) => draft.uplifts.find(u => u.vehicleType === vt)?.uplift ?? 0;

  // ── Validation mirrored from the server, so problems surface before saving ──
  const validationError = useMemo(() => {
    if (!draft.appliesToSameDay && !draft.appliesToScheduled)
      return 'Choose at least one of same-day or scheduled work, otherwise the card matches nothing.';
    if (draft.isEnabled && draft.bands.length === 0)
      return 'Add at least one distance band before switching auto-quote on.';
    if (draft.isEnabled && draft.areas.length === 0)
      return 'Add at least one coverage area before switching auto-quote on.';
    if (draft.workingHoursFrom >= draft.workingHoursTo)
      return 'Working hours must start before they end.';
    return null;
  }, [draft]);

  if (isLoading) {
    return (
      <div className="p-8 flex items-center gap-2 text-gray-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading your rate card…
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-4" data-testid="auto-quote-page">

      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Zap className="w-6 h-6 text-blue-600" /> Auto-Quote
          </h1>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Set your prices once. When a matching job is posted we price it from this card and
            quote on your behalf — you can change the amount at any time before it's accepted.
          </p>
        </div>
        <button
          onClick={() => saveMutation.mutate(draft)}
          disabled={saveMutation.isPending || validationError !== null}
          data-testid="auto-quote-save"
          className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white font-semibold px-5 py-2.5 rounded-lg text-sm transition-colors flex items-center gap-2"
        >
          {saveMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
          Save rate card
        </button>
      </div>

      {(error || validationError) && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2" role="alert">
          <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
          <p className="text-sm text-red-700">{error ?? validationError}</p>
        </div>
      )}
      {saved && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-emerald-600" />
          <p className="text-sm text-emerald-700">Rate card saved.</p>
        </div>
      )}

      {/* ── Switch + mode ── */}
      <Section title="Switch it on" subtitle="How your quotes reach the customer">
        <div className="space-y-4 pt-3">
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={draft.isEnabled}
              onChange={e => patch('isEnabled', e.target.checked)}
              data-testid="auto-quote-enabled"
              className="mt-1 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            />
            <span>
              <span className="text-sm font-medium text-gray-900">Auto-quote matching jobs</span>
              <span className="block text-xs text-gray-500">
                Off by default. Try the simulator below first — it shows exactly what this card would quote.
              </span>
            </span>
          </label>

          <div className="grid sm:grid-cols-2 gap-3">
            {[
              {
                mode : AutoQuoteMode.ConfirmFirst,
                title: 'Review before sending',
                body : 'We price the job and notify you. The customer sees nothing until you send it.',
              },
              {
                mode : AutoQuoteMode.Instant,
                title: 'Send immediately',
                body : 'The customer sees your quote straight away. Fastest, but you are committed to the price.',
              },
            ].map(opt => (
              <button
                key={opt.mode}
                type="button"
                onClick={() => patch('mode', opt.mode)}
                data-testid={`auto-quote-mode-${opt.mode}`}
                className={`text-left p-3 rounded-lg border-2 transition-colors ${
                  draft.mode === opt.mode
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <p className="text-sm font-semibold text-gray-900">{opt.title}</p>
                <p className="text-xs text-gray-500 mt-0.5">{opt.body}</p>
              </button>
            ))}
          </div>

          {draft.mode === AutoQuoteMode.ConfirmFirst && (
            <label className="block">
              <span className="text-xs font-medium text-gray-600">
                Send automatically if I haven't reviewed it within (minutes)
              </span>
              <input
                type="number"
                min={1}
                max={1440}
                placeholder="Leave blank to always require review"
                value={draft.autoReleaseAfterMinutes ?? ''}
                onChange={e =>
                  patch('autoReleaseAfterMinutes', e.target.value === '' ? null : parseInt(e.target.value, 10))
                }
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              />
            </label>
          )}

          <div className="flex flex-wrap gap-4 pt-1">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={draft.appliesToSameDay}
                onChange={e => patch('appliesToSameDay', e.target.checked)}
                data-testid="auto-quote-sameday"
                className="w-4 h-4 rounded border-gray-300 text-blue-600"
              />
              <span className="text-sm text-gray-700">Same-day work</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={draft.appliesToScheduled}
                onChange={e => patch('appliesToScheduled', e.target.checked)}
                data-testid="auto-quote-scheduled"
                className="w-4 h-4 rounded border-gray-300 text-blue-600"
              />
              <span className="text-sm text-gray-700">Scheduled work</span>
            </label>
          </div>
        </div>
      </Section>

      {/* ── Coverage ── */}
      <Section title="Where you'll quote" subtitle="Add as many postcodes as you like, each with its own radius">
        <div className="pt-3 space-y-3">
          <div className="flex flex-wrap gap-2 items-end">
            <label className="flex-1 min-w-[180px]">
              <span className="text-xs font-medium text-gray-600">Postcode or district</span>
              <input
                value={areaPostcode}
                onChange={e => setAreaPostcode(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); void addArea(); } }}
                placeholder="LS1 or LS1 4AP"
                data-testid="auto-quote-area-postcode"
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm uppercase"
              />
            </label>
            <label>
              <span className="text-xs font-medium text-gray-600">Radius</span>
              <select
                value={areaRadius}
                onChange={e => setAreaRadius(parseInt(e.target.value, 10))}
                className="mt-1 px-3 py-2 border border-gray-200 rounded-lg text-sm"
              >
                {RADII.map(r => <option key={r} value={r}>{r} miles</option>)}
              </select>
            </label>
            <button
              type="button"
              onClick={() => void addArea()}
              disabled={areaBusy || !areaPostcode.trim()}
              data-testid="auto-quote-area-add"
              className="bg-gray-900 hover:bg-gray-800 disabled:bg-gray-300 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-1.5"
            >
              {areaBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Add
            </button>
          </div>

          {draft.areas.length === 0 ? (
            <p className="text-xs text-gray-400 italic">
              No coverage areas yet. Auto-quote needs at least one before it can be switched on.
            </p>
          ) : (
            <ul className="flex flex-wrap gap-2" data-testid="auto-quote-area-list">
              {draft.areas.map(a => (
                <li
                  key={`${a.postcode}-${a.radiusMiles}`}
                  className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-full pl-3 pr-1.5 py-1"
                >
                  <MapPin className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-sm text-gray-700">{a.postcode}</span>
                  <span className="text-xs text-gray-400">{a.radiusMiles} mi</span>
                  <button
                    type="button"
                    onClick={() => removeArea(a)}
                    aria-label={`Remove ${a.postcode}`}
                    className="p-1 rounded-full hover:bg-red-50 text-gray-400 hover:text-red-600"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}

          <MoneyField
            label="Longest job I'll take (miles) — 0 for no limit"
            suffix="mi"
            value={draft.maxJobDistanceMiles}
            onChange={v => patch('maxJobDistanceMiles', Math.round(v))}
          />
        </div>
      </Section>

      {/* ── Distance bands ── */}
      <Section
        title="Distance pricing"
        subtitle="Each band charges only the miles inside it, so your price rises smoothly"
      >
        <div className="pt-3 space-y-2" data-testid="auto-quote-bands">
          {draft.bands.map((band, i) => (
            <div key={band.bandOrder} className="flex flex-wrap items-end gap-2 bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-500 w-full sm:w-auto">
                From <strong className="text-gray-800">{band.fromMiles} mi</strong>
              </div>
              <label className="flex-1 min-w-[110px]">
                <span className="text-[11px] font-medium text-gray-600">Up to (miles)</span>
                <input
                  type="number"
                  min={band.fromMiles + 1}
                  placeholder={i === draft.bands.length - 1 ? 'No limit' : ''}
                  value={band.toMiles ?? ''}
                  onChange={e =>
                    setBandBoundary(i, e.target.value === '' ? null : parseFloat(e.target.value))
                  }
                  disabled={i === draft.bands.length - 1}
                  className="mt-1 w-full px-2.5 py-1.5 border border-gray-200 rounded-lg text-sm disabled:bg-gray-100 disabled:text-gray-400"
                />
              </label>
              <label className="flex-1 min-w-[110px]">
                <span className="text-[11px] font-medium text-gray-600">Rate per mile</span>
                <div className="mt-1 relative">
                  <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">£</span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={band.ratePerMile}
                    onChange={e => setBandRate(i, parseFloat(e.target.value) || 0)}
                    className="w-full pl-6 pr-2 py-1.5 border border-gray-200 rounded-lg text-sm"
                  />
                </div>
              </label>
              <button
                type="button"
                onClick={() => removeBand(i)}
                disabled={draft.bands.length <= 1}
                aria-label={`Remove band ${i + 1}`}
                className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addBand}
            data-testid="auto-quote-band-add"
            className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
          >
            <Plus className="w-4 h-4" /> Add band
          </button>

          <div className="flex items-start gap-2 bg-blue-50 border border-blue-100 rounded-lg p-3 mt-2">
            <Info className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
            <p className="text-xs text-blue-800">
              There is no minimum quote. Your callout fee and first band are what keep very short
              jobs worthwhile — try a 2-mile job in the simulator to check.
            </p>
          </div>
        </div>
      </Section>

      {/* ── Fees and surcharges ── */}
      <Section title="Fees and surcharges" defaultOpen={false}>
        <div className="pt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <MoneyField label="Callout fee"      value={draft.calloutFee}       onChange={v => patch('calloutFee', v)}
                      hint="Charged on every job" />
          <MoneyField label="Per extra stop"   value={draft.perExtraStopFee}  onChange={v => patch('perExtraStopFee', v)} />
          <MoneyField label="Per tonne"        value={draft.perTonneRate}     onChange={v => patch('perTonneRate', v)} />
          <MoneyField label="Per loading metre" value={draft.perLdmRate}      onChange={v => patch('perLdmRate', v)} />
          <MoneyField label="Tail lift"        value={draft.tailLiftSurcharge} onChange={v => patch('tailLiftSurcharge', v)} />
          <MoneyField label="Two-man"          value={draft.twoManSurcharge}  onChange={v => patch('twoManSurcharge', v)} />
          <MoneyField label="HIAB"             value={draft.hiabSurcharge}    onChange={v => patch('hiabSurcharge', v)} />
          <MoneyField label="Same-day"         value={draft.sameDaySurcharge} onChange={v => patch('sameDaySurcharge', v)} />
          <MoneyField label="Out of hours"     value={draft.outOfHoursSurcharge} onChange={v => patch('outOfHoursSurcharge', v)} />
          <MoneyField label="Weekend"          value={draft.weekendSurcharge} onChange={v => patch('weekendSurcharge', v)} />
          <MoneyField label="Bank holiday"     value={draft.bankHolidaySurcharge} onChange={v => patch('bankHolidaySurcharge', v)}
                      hint="Replaces out-of-hours, never both" />
          <MoneyField label="Fuel levy"        value={draft.fuelLevyPercent}  onChange={v => patch('fuelLevyPercent', v)}
                      suffix="%" hint="Applied to the running subtotal" />

          <label className="block">
            <span className="text-xs font-medium text-gray-600">Working hours</span>
            <div className="mt-1 flex items-center gap-2">
              <input type="number" min={0} max={23} value={draft.workingHoursFrom}
                     onChange={e => patch('workingHoursFrom', parseInt(e.target.value, 10) || 0)}
                     className="w-16 px-2 py-2 border border-gray-200 rounded-lg text-sm" />
              <span className="text-gray-400 text-sm">to</span>
              <input type="number" min={1} max={24} value={draft.workingHoursTo}
                     onChange={e => patch('workingHoursTo', parseInt(e.target.value, 10) || 0)}
                     className="w-16 px-2 py-2 border border-gray-200 rounded-lg text-sm" />
            </div>
          </label>

          <label className="block">
            <span className="text-xs font-medium text-gray-600">Round quotes to</span>
            <select
              value={draft.roundToNearestPence}
              onChange={e => patch('roundToNearestPence', parseInt(e.target.value, 10))}
              className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
            >
              <option value={0}>No rounding</option>
              <option value={100}>Nearest £1</option>
              <option value={500}>Nearest £5</option>
              <option value={1000}>Nearest £10</option>
            </select>
          </label>
        </div>
      </Section>

      {/* ── Vehicle uplifts ── */}
      <Section title="Vehicle uplifts" subtitle="Added when a job needs a particular class" defaultOpen={false}>
        <div className="pt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {VEHICLE_OPTIONS.map(v => (
            <MoneyField
              key={v.value}
              label={v.label}
              value={upliftFor(v.value)}
              onChange={n => setUplift(v.value, n)}
            />
          ))}
        </div>
      </Section>

      {/* ── Running costs and limits ── */}
      <Section title="Running costs and limits" defaultOpen={false}>
        <div className="pt-3 space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs font-medium text-gray-600">
                Recover road charges at
              </span>
              <select
                value={draft.zoneChargeRecoveryFactor}
                onChange={e => patch('zoneChargeRecoveryFactor', parseFloat(e.target.value))}
                className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
              >
                <option value={1}>Full — I rarely enter charging zones</option>
                <option value={0.5}>Half — I'm often in a zone anyway</option>
                <option value={0.25}>Quarter</option>
                <option value={0}>Don't recover them</option>
              </select>
              <span className="text-[11px] text-gray-400 mt-1 block">
                ULEZ and the Congestion Charge are billed per day, not per job, so recovering
                them in full on every job can price you out if you're in the zone daily.
              </span>
            </label>

            <div className="space-y-2 pt-5">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={draft.hasDartChargeAccount}
                       onChange={e => patch('hasDartChargeAccount', e.target.checked)}
                       className="w-4 h-4 rounded border-gray-300 text-blue-600" />
                <span className="text-sm text-gray-700">I have a Dart Charge account</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={draft.hasTflAutoPay}
                       onChange={e => patch('hasTflAutoPay', e.target.checked)}
                       className="w-4 h-4 rounded border-gray-300 text-blue-600" />
                <span className="text-sm text-gray-700">I'm on TfL Auto Pay</span>
              </label>
              <p className="text-[11px] text-gray-400">
                Account holders pay less. Telling us stops your quotes being padded with the
                higher casual rate.
              </p>
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-3">
            <MoneyField label="Most open auto-quotes at once (0 = no limit)" suffix="#"
                        value={draft.maxOpenAutoQuotes} onChange={v => patch('maxOpenAutoQuotes', Math.round(v))} />
            <MoneyField label="Most auto-quotes per day (0 = no limit)" suffix="#"
                        value={draft.dailyAutoQuoteCap} onChange={v => patch('dailyAutoQuoteCap', Math.round(v))} />
          </div>
        </div>
      </Section>

      {/* Workshops price standard services from a catalogue rather than a distance card,
          so point them at it rather than leaving the two settings unconnected. */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-semibold text-gray-900">Workshop? Price your standard services</h2>
          <p className="text-xs text-gray-500 mt-0.5 max-w-lg">
            MOTs, servicing and tyres can be quoted without seeing the vehicle. Set a price once
            and we&apos;ll quote them for you.
          </p>
        </div>
        <a
          href="/workshop-services"
          data-testid="auto-quote-workshop-services-link"
          className="px-4 py-2 rounded-lg border border-gray-200 hover:bg-gray-50 text-sm font-semibold text-gray-700 whitespace-nowrap"
        >
          Standard services
        </a>
      </div>

      {/* ── Simulator ── */}
      <Simulator svc={svc} hasSavedRule={rule !== null && rule !== undefined} />
    </div>
  );
}

// ─────────────────────────────────────────────
//  SIMULATOR
//
//  Providers will not switch on a formula they cannot test. This runs against the
//  SAVED card, not the unsaved form — it has to agree with what dispatch would
//  actually do, and a preview of an unsaved draft would not.
// ─────────────────────────────────────────────

type Preset = {
  key: string; label: string; miles: number; vehicle: VehicleType;
  sameDay?: boolean; hour?: number; tailLift?: boolean; stops?: number;
};

const PRESETS: Preset[] = [
  { key: 'local',   label: 'Local 8-mile van job',   miles: 8,   vehicle: VehicleType.SmallVan, hour: 10 },
  { key: 'trunk',   label: '120-mile trunk run',     miles: 120, vehicle: VehicleType.Tonne7_5, hour: 9 },
  { key: 'sameday', label: 'Same-day 25 miles',      miles: 25,  vehicle: VehicleType.LWBVan, sameDay: true, hour: 14 },
  { key: 'ooh',     label: 'Out of hours, tail lift', miles: 40, vehicle: VehicleType.LutonVan, hour: 22, tailLift: true },
  { key: 'multi',   label: 'Multi-drop, 3 extra stops', miles: 30, vehicle: VehicleType.LWBVan, hour: 11, stops: 3 },
];

function Simulator({
  svc, hasSavedRule,
}: {
  svc: ReturnType<typeof autoQuoteService>;
  hasSavedRule: boolean;
}) {
  const [miles, setMiles]       = useState(42);
  const [vehicle, setVehicle]   = useState<VehicleType>(VehicleType.LWBVan);
  const [hour, setHour]         = useState(10);
  const [sameDay, setSameDay]   = useState(false);
  const [tailLift, setTailLift] = useState(false);
  const [twoMan, setTwoMan]     = useState(false);
  const [stops, setStops]       = useState(0);

  const [result, setResult] = useState<AutoQuotePreviewDto | null>(null);
  const [err, setErr]       = useState<string | null>(null);
  const [busy, setBusy]     = useState(false);

  const run = useCallback(async (override?: Partial<Preset>) => {
    setBusy(true);
    setErr(null);
    try {
      const scheduled = new Date();
      scheduled.setHours(override?.hour ?? hour, 0, 0, 0);
      const data = await svc.preview({
        requiredVehicleType: override?.vehicle ?? vehicle,
        distanceMiles      : override?.miles ?? miles,
        scheduledAt        : scheduled.toISOString(),
        isSameDay          : override?.sameDay ?? sameDay,
        extraStopCount     : override?.stops ?? stops,
        requiresTailLift   : override?.tailLift ?? tailLift,
        requiresTwoMan     : twoMan,
      });
      setResult(data);
    } catch (e) {
      const msg = (e as { response?: { data?: { message?: string; detail?: string } } })?.response?.data;
      setErr(msg?.message ?? msg?.detail ?? 'Could not run the simulator.');
      setResult(null);
    } finally {
      setBusy(false);
    }
  }, [svc, miles, vehicle, hour, sameDay, tailLift, twoMan, stops]);

  const applyPreset = (p: Preset) => {
    setMiles(p.miles);
    setVehicle(p.vehicle);
    setHour(p.hour ?? 10);
    setSameDay(p.sameDay ?? false);
    setTailLift(p.tailLift ?? false);
    setStops(p.stops ?? 0);
    void run(p);
  };

  return (
    <Section title="Simulator" subtitle="See exactly what this card would quote before you switch it on">
      <div className="pt-3 space-y-4" data-testid="auto-quote-simulator">

        {!hasSavedRule && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
            <Info className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <p className="text-xs text-amber-800">
              Save your rate card first — the simulator runs against the saved version so it
              matches what we'd actually quote.
            </p>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {PRESETS.map(p => (
            <button
              key={p.key}
              type="button"
              onClick={() => applyPreset(p)}
              data-testid={`auto-quote-preset-${p.key}`}
              className="text-xs px-3 py-1.5 rounded-full border border-gray-200 hover:border-blue-400 hover:bg-blue-50 text-gray-700"
            >
              {p.label}
            </button>
          ))}
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <label className="block">
            <span className="text-xs font-medium text-gray-600">Distance (miles)</span>
            <input type="number" min={0} value={miles}
                   onChange={e => setMiles(parseFloat(e.target.value) || 0)}
                   data-testid="simulator-miles"
                   className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-gray-600">Vehicle</span>
            <select value={vehicle} onChange={e => setVehicle(parseInt(e.target.value, 10) as VehicleType)}
                    className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm">
              {VEHICLE_OPTIONS.map(v => <option key={v.value} value={v.value}>{v.label}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-gray-600">Time of day</span>
            <input type="number" min={0} max={23} value={hour}
                   onChange={e => setHour(parseInt(e.target.value, 10) || 0)}
                   className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-gray-600">Extra stops</span>
            <input type="number" min={0} value={stops}
                   onChange={e => setStops(parseInt(e.target.value, 10) || 0)}
                   className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
          </label>
        </div>

        <div className="flex flex-wrap gap-4">
          {[
            { label: 'Same-day', v: sameDay,  set: setSameDay },
            { label: 'Tail lift', v: tailLift, set: setTailLift },
            { label: 'Two-man',   v: twoMan,   set: setTwoMan },
          ].map(c => (
            <label key={c.label} className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={c.v} onChange={e => c.set(e.target.checked)}
                     className="w-4 h-4 rounded border-gray-300 text-blue-600" />
              <span className="text-sm text-gray-700">{c.label}</span>
            </label>
          ))}
        </div>

        <button
          type="button"
          onClick={() => void run()}
          disabled={busy}
          data-testid="simulator-run"
          className="bg-gray-900 hover:bg-gray-800 disabled:bg-gray-300 text-white px-4 py-2 rounded-lg text-sm flex items-center gap-2"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calculator className="w-4 h-4" />}
          Work out the price
        </button>

        {err && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 shrink-0" />
            <p className="text-sm text-red-700">{err}</p>
          </div>
        )}

        {result && (
          <div className="border border-gray-200 rounded-xl overflow-hidden" data-testid="simulator-result">
            <div className="bg-gray-900 text-white px-4 py-3 flex items-baseline justify-between">
              <span className="text-sm">You would quote</span>
              <span className="text-2xl font-bold" data-testid="simulator-total">{money(result.amount)}</span>
            </div>
            <table className="w-full text-sm">
              <tbody>
                {result.lines.map((l, i) => (
                  <tr key={`${l.code}-${i}`} className="border-b border-gray-100 last:border-0">
                    <td className="px-4 py-2 text-gray-600">{l.label}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-gray-800">{money(l.amount)}</td>
                  </tr>
                ))}
                <tr className="bg-gray-50 border-t border-gray-200">
                  <td className="px-4 py-2 text-gray-600">Platform fee</td>
                  <td className="px-4 py-2 text-right tabular-nums text-red-600">
                    −{money(result.platformCommission)}
                  </td>
                </tr>
                <tr className="bg-emerald-50">
                  <td className="px-4 py-2.5 font-semibold text-gray-900">You keep</td>
                  <td className="px-4 py-2.5 text-right tabular-nums font-bold text-emerald-700"
                      data-testid="simulator-net">
                    {money(result.netAmount)}
                  </td>
                </tr>
              </tbody>
            </table>
            <p className="px-4 py-2 text-[11px] text-gray-400 bg-white border-t border-gray-100">
              Road charges are included in the total but are never itemised to the customer —
              they see one figure.
            </p>
          </div>
        )}
      </div>
    </Section>
  );
}
